/**
 * THE postal-address module. One canonical shape, one validator, one autofill
 * table — shared by S6 (order details) and My Account (profile default).
 *
 * FIELD-NAME CONTRACT
 * -------------------
 * Canonical keys: line1, line2, city, region, postcode, country.
 * Legacy keys (`state`, `zip`) are accepted on READ and NEVER written. Every
 * write goes through `canonicalAddress()`, so a stored address can never carry
 * both `region` and `state` (the hybrid that produced the blank-State bug).
 * The server repeats this normalisation in `public.normalise_postal_address`,
 * so a client bypass cannot persist a hybrid either.
 */

export interface AddressDraft {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
}

export const EMPTY_ADDRESS: AddressDraft = {
  line1: "",
  line2: "",
  city: "",
  region: "",
  postcode: "",
  country: "US",
};

/**
 * CONVENIENCE ONLY — a tiny local ZIP table, not a lookup service. A miss means
 * "we couldn't autofill", NOT "this ZIP is invalid". Nothing in validation or
 * submission may depend on a hit.
 */
export const ZIP_AUTOFILL: Record<string, [string, string]> = {
  "10001": ["New York", "NY"],
  "11201": ["Brooklyn", "NY"],
  "90210": ["Beverly Hills", "CA"],
  "94110": ["San Francisco", "CA"],
  "60601": ["Chicago", "IL"],
  "33139": ["Miami Beach", "FL"],
  "78701": ["Austin", "TX"],
  "98101": ["Seattle", "WA"],
  "30303": ["Atlanta", "GA"],
  "02108": ["Boston", "MA"],
};

/** ZIP is validated as a FORMAT — five digits. No network call, no gate. */
export const isZipFormat = (v: string) => /^\d{5}$/.test((v ?? "").trim());

/**
 * Country is normalised to ISO-3166-1 alpha-2 on READ and WRITE — the client
 * mirror of `public.normalise_country`. A raw 'USA' silently misses a routing
 * rule scoped to 'US' and the lead falls through to default with a clean-looking
 * audit trail (INV-ROUTE-1), so the raw form must never leave this module.
 */
const COUNTRY_ALIASES: Record<string, string> = {
  US: "US",
  USA: "US",
  UNITEDSTATES: "US",
  UNITEDSTATESOFAMERICA: "US",
  AMERICA: "US",
  UK: "GB",
  GB: "GB",
  GBR: "GB",
  UNITEDKINGDOM: "GB",
  GREATBRITAIN: "GB",
  ENGLAND: "GB",
  CA: "CA",
  CAN: "CA",
  CANADA: "CA",
  AU: "AU",
  AUS: "AU",
  AUSTRALIA: "AU",
  IE: "IE",
  IRL: "IE",
  IRELAND: "IE",
  NZ: "NZ",
  NZL: "NZ",
  NEWZEALAND: "NZ",
};

export function normaliseCountry(value: string | null | undefined): string {
  const raw = (value ?? "").toUpperCase().replace(/[^A-Z]/g, "");
  if (!raw) return "US";
  return COUNTRY_ALIASES[raw] ?? raw;
}

/**
 * DELIVERY MARKETS — the only countries a select may emit, as alpha-2.
 *
 * Today this list has ONE entry, so the country control renders as a fixed
 * value rather than a dropdown: a one-option select is a lie about choice. Add
 * a market here and the same control becomes a real dropdown with no other
 * change — that is the whole extensibility mechanism.
 */
export const SUPPORTED_COUNTRIES: { code: string; label: string }[] = [
  { code: "US", label: "United States" },
];

export const countryLabel = (code: string) =>
  SUPPORTED_COUNTRIES.find((c) => c.code === normaliseCountry(code))?.label ?? code;

/**
 * US states, DC and the postal territories, emitting the 2-letter code the
 * server CHECKs (`^[A-Z]{2}$`). A select makes "Texas" and "tx" unenterable
 * instead of merely correctable — but the server constraint still stands,
 * because the API is reachable without this form.
 */
export const US_STATES: { code: string; label: string }[] = [
  { code: "AL", label: "Alabama" },
  { code: "AK", label: "Alaska" },
  { code: "AZ", label: "Arizona" },
  { code: "AR", label: "Arkansas" },
  { code: "CA", label: "California" },
  { code: "CO", label: "Colorado" },
  { code: "CT", label: "Connecticut" },
  { code: "DE", label: "Delaware" },
  { code: "DC", label: "District of Columbia" },
  { code: "FL", label: "Florida" },
  { code: "GA", label: "Georgia" },
  { code: "HI", label: "Hawaii" },
  { code: "ID", label: "Idaho" },
  { code: "IL", label: "Illinois" },
  { code: "IN", label: "Indiana" },
  { code: "IA", label: "Iowa" },
  { code: "KS", label: "Kansas" },
  { code: "KY", label: "Kentucky" },
  { code: "LA", label: "Louisiana" },
  { code: "ME", label: "Maine" },
  { code: "MD", label: "Maryland" },
  { code: "MA", label: "Massachusetts" },
  { code: "MI", label: "Michigan" },
  { code: "MN", label: "Minnesota" },
  { code: "MS", label: "Mississippi" },
  { code: "MO", label: "Missouri" },
  { code: "MT", label: "Montana" },
  { code: "NE", label: "Nebraska" },
  { code: "NV", label: "Nevada" },
  { code: "NH", label: "New Hampshire" },
  { code: "NJ", label: "New Jersey" },
  { code: "NM", label: "New Mexico" },
  { code: "NY", label: "New York" },
  { code: "NC", label: "North Carolina" },
  { code: "ND", label: "North Dakota" },
  { code: "OH", label: "Ohio" },
  { code: "OK", label: "Oklahoma" },
  { code: "OR", label: "Oregon" },
  { code: "PA", label: "Pennsylvania" },
  { code: "RI", label: "Rhode Island" },
  { code: "SC", label: "South Carolina" },
  { code: "SD", label: "South Dakota" },
  { code: "TN", label: "Tennessee" },
  { code: "TX", label: "Texas" },
  { code: "UT", label: "Utah" },
  { code: "VT", label: "Vermont" },
  { code: "VA", label: "Virginia" },
  { code: "WA", label: "Washington" },
  { code: "WV", label: "West Virginia" },
  { code: "WI", label: "Wisconsin" },
  { code: "WY", label: "Wyoming" },
  { code: "AS", label: "American Samoa" },
  { code: "GU", label: "Guam" },
  { code: "MP", label: "Northern Mariana Islands" },
  { code: "PR", label: "Puerto Rico" },
  { code: "VI", label: "U.S. Virgin Islands" },
];

export const isUsStateCode = (v: string | null | undefined) =>
  US_STATES.some((s) => s.code === (v ?? "").trim().toUpperCase());


/** READ side: tolerate legacy keys, produce a canonical draft. */
export function addressDraftFrom(
  address: Record<string, string> | null | undefined,
): AddressDraft {
  if (!address) return { ...EMPTY_ADDRESS };
  return {
    line1: address.line1 ?? "",
    line2: address.line2 ?? "",
    city: address.city ?? "",
    region: (address.region || address.state || "").toUpperCase(),
    postcode: address.postcode || address.zip || "",
    country: normaliseCountry(address.country),
  };
}

/**
 * WRITE side: canonical keys ONLY. Legacy keys are dropped, not merged, so the
 * output is never a `region`+`state` hybrid. Returns null for an empty draft.
 */
export function canonicalAddress(draft: AddressDraft): Record<string, string> | null {
  const out = {
    line1: draft.line1.trim(),
    line2: draft.line2.trim(),
    city: draft.city.trim(),
    region: draft.region.trim().toUpperCase(),
    postcode: draft.postcode.trim(),
    country: normaliseCountry(draft.country),
  };
  if (!out.line1 && !out.city && !out.region && !out.postcode) return null;
  return out;
}

/** The postal contract: street + city + state + well-formed ZIP. */
export function isAddressComplete(draft: AddressDraft): boolean {
  return (
    Boolean(draft.line1.trim()) &&
    Boolean(draft.city.trim()) &&
    Boolean(draft.region.trim()) &&
    isZipFormat(draft.postcode)
  );
}

/** Named missing fields — every name here has an input on the calling screen. */
export function addressMissingFields(draft: AddressDraft): string[] {
  const missing: string[] = [];
  if (!draft.line1.trim()) missing.push("Street address");
  if (!draft.city.trim()) missing.push("City");
  if (!draft.region.trim()) missing.push("State");
  if (!isZipFormat(draft.postcode)) missing.push("ZIP code (5 digits)");
  return missing;
}
